"use client";
import "mapbox-gl/dist/mapbox-gl.css";
import React, { useEffect, useRef, useState } from "react";
import Twemoji from "react-twemoji";
import { config } from "./config";
import Disclaimer from "./Disclaimer";
import Join from "./Join";
import PMSuggestion from "./PMSuggestion";
import ReportScreen from "./Report";
import {
  BusClusterChain,
  Eviction,
  GroupedLocation,
  NewAPIResponse,
  PropertyAddress,
  PropertyLocation,
  RelatedProperty,
  RelatedPropertyByCluster,
} from "./ResultTypes";
import Reviews from "./Reviews";
import "./styles/App.scss";
import "./styles/Result.scss";
import { SuggestionBox } from "./Suggestion";
import Survey from "./Survey";
import { encode } from "./utils";
import VacancySuggestion from "./VacancySuggestion";
var mapboxgl = require("mapbox-gl/dist/mapbox-gl.js");

function convertToUSD(value: number) {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  });
  return formatter.format(value);
}

function getEvictionRange(
  evictions: Eviction[],
  evictions_by_cluster: NewAPIResponse["result"]["evictions_by_cluster"],
): string {
  if (!evictions && !evictions_by_cluster) return "";

  const allEvictions = [...(evictions || []), ...(evictions_by_cluster || [])];

  // Remove duplicates by case_code
  const uniqueEvictions = allEvictions.filter(
    (ev, index, self) =>
      index === self.findIndex((e) => e.case_code === ev.case_code),
  );

  const highConfidence = uniqueEvictions.filter(
    (ev) => ev.similarity === null || ev.similarity >= 0.9,
  ).length;
  const lowConfidence = uniqueEvictions.filter(
    (ev) => ev.similarity !== null && ev.similarity < 0.9,
  ).length;

  if (lowConfidence === 0) {
    return `${highConfidence} ${
      highConfidence === 1 ? "EVICTION" : "EVICTIONS"
    } FILED`;
  }

  return `${highConfidence} - ${
    highConfidence + lowConfidence
  } EVICTION RECORDS RELATED TO THIS OWNER`;
}

// Helper to check if a type is by_business::xxxx::by_address pattern
function isByBusinessByAddressType(type: string): boolean {
  return /^by_business::[A-Z0-9]+::by_address$/i.test(type);
}

// // ============================================
// // RELATIONSHIP ARROW TYPES AND UTILITIES
// // ============================================

// interface PropertyRelationship {
//   fromPropertyId: string;
//   toPropertyId: string;
//   fromLocation: PropertyLocation;
//   toLocation: PropertyLocation;
//   matchType: string;
//   matchKey: string;
//   matchTo?: string;
//   confidence: number;
// }

// interface ArrowTooltipInfo {
//   x: number;
//   y: number;
//   relationship: PropertyRelationship;
//   visible: boolean;
// }

// // Format match type for human-readable display
// function formatMatchType(matchType: string): string {
//   switch (matchType) {
//     case "src_owner":
//       return "Same Source Owner";
//     case "address_1":
//       return "Matching Owner Address";
//     case "geocoded_address":
//       return "Same Geocoded Address";
//     case "people_owner":
//       return "Same Person Owner";
//     case "source":
//       return "Source Property";
//     default:
//       if (matchType.startsWith("by_business::")) {
//         const parts = matchType.split("::");
//         if (parts.length >= 3) {
//           return `Business Link (${parts[1]}) via ${parts[2]}`;
//         }
//         return `Business Connection`;
//       }
//       return matchType
//         .replace(/_/g, " ")
//         .replace(/\b\w/g, (c) => c.toUpperCase());
//   }
// }

// // Generate explanation text for a relationship
// function generateExplanation(rel: PropertyRelationship): string {
//   const matchType = rel.matchType;
//   const confidence = Math.round(rel.confidence * 100);

//   let explanation = "";

//   switch (matchType) {
//     case "src_owner":
//       explanation = `These properties share the same registered owner name: "${rel.matchKey}"`;
//       break;
//     case "address_1":
//       explanation = `The owner addresses match: "${rel.matchKey}"`;
//       if (rel.matchTo && rel.matchTo !== rel.matchKey) {
//         explanation += ` ↔ "${rel.matchTo}"`;
//       }
//       break;
//     case "geocoded_address":
//       explanation = `Both properties' owner addresses geocode to the same location: "${rel.matchKey}"`;
//       break;
//     case "people_owner":
//       explanation = `Same person listed as owner: "${rel.matchKey}"`;
//       if (rel.matchTo) {
//         explanation += `\nMatched to: "${rel.matchTo}"`;
//       }
//       break;
//     case "source":
//       explanation = "This is the property you searched for";
//       break;
//     default:
//       if (matchType.startsWith("by_business::")) {
//         const parts = matchType.split("::");
//         if (parts.length >= 3) {
//           const regNum = parts[1];
//           const relationType = parts[2];
//           explanation = `Linked through business entity (Reg#: ${regNum}) via ${relationType}`;
//         } else {
//           explanation = `Connected through a shared business entity`;
//         }
//       } else {
//         explanation = `Connection type: ${formatMatchType(matchType)}`;
//       }
//   }

//   if (matchType !== "source") {
//     explanation += `\n\nConfidence: ${confidence}%`;
//   }

//   return explanation;
// }
// // Calculate arrow path between two points (straight line)
// function calculateArrowPath(
//   fromLngLat: [number, number],
//   toLngLat: [number, number],
//   map: any
// ): { path: string; midpoint: { x: number; y: number }; angle: number } | null {
//   if (!map) return null;

//   const fromPixel = map.project(fromLngLat);
//   const toPixel = map.project(toLngLat);

//   const dx = toPixel.x - fromPixel.x;
//   const dy = toPixel.y - fromPixel.y;
//   const distance = Math.sqrt(dx * dx + dy * dy);

//   // Skip if points are too close
//   if (distance < 30) return null;

//   // Build SVG path (straight line)
//   const path = `M ${fromPixel.x} ${fromPixel.y} L ${toPixel.x} ${toPixel.y}`;

//   // Calculate midpoint (simple average for straight line)
//   const midX = (fromPixel.x + toPixel.x) / 2;
//   const midY = (fromPixel.y + toPixel.y) / 2;

//   // Calculate angle for arrowhead
//   const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

//   return {
//     path,
//     midpoint: { x: midX, y: midY },
//     angle,
//   };
// }

// // ============================================
// // RELATIONSHIP ARROW OVERLAY COMPONENT
// // ============================================

// interface RelationshipArrowsProps {
//   relationships: PropertyRelationship[];
//   map: any;
//   containerRef: React.RefObject<HTMLDivElement>;
// }

// function RelationshipArrows({
//   relationships,
//   map,
//   containerRef,
// }: RelationshipArrowsProps) {
//   const [arrows, setArrows] = useState<
//     Array<{
//       path: string;
//       midpoint: { x: number; y: number };
//       angle: number;
//       relationship: PropertyRelationship;
//     }>
//   >([]);
//   const [tooltip, setTooltip] = useState<ArrowTooltipInfo | null>(null);
//   const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
//   const svgContainerRef = useRef<HTMLDivElement | null>(null);

//   // Create and inject SVG container into Mapbox's control container
//   useEffect(() => {
//     if (!map || !containerRef.current) return;

//     // Create a container div for our SVG overlay
//     const container = document.createElement("div");
//     container.className = "relationship-arrows-container";
//     container.style.cssText = `
//       position: absolute;
//       top: 0;
//       left: 0;
//       width: 100%;
//       height: 100%;
//       pointer-events: none;
//       z-index: 1000;
//       background: transparent;
//     `;

//     // Append to the map container (not the mapbox internal canvas)
//     containerRef.current.appendChild(container);
//     svgContainerRef.current = container;

//     return () => {
//       if (container.parentNode) {
//         container.parentNode.removeChild(container);
//       }
//     };
//   }, [map, containerRef]);

//   // Update arrows when map moves/zooms
//   const updateArrows = useCallback(() => {
//     if (!map || !containerRef.current) return;

//     const rect = containerRef.current.getBoundingClientRect();
//     setDimensions({ width: rect.width, height: rect.height });

//     const newArrows: typeof arrows = [];

//     relationships.forEach((rel) => {
//       if (!rel.fromLocation?.longitude || !rel.toLocation?.longitude) return;

//       const fromLngLat: [number, number] = [
//         rel.fromLocation.longitude,
//         rel.fromLocation.latitude,
//       ];
//       const toLngLat: [number, number] = [
//         rel.toLocation.longitude,
//         rel.toLocation.latitude,
//       ];

//       const arrowData = calculateArrowPath(fromLngLat, toLngLat, map);
//       if (arrowData) {
//         newArrows.push({
//           ...arrowData,
//           relationship: rel,
//         });
//       }
//     });

//     setArrows(newArrows);
//   }, [relationships, map, containerRef]);

//   useEffect(() => {
//     if (!map) return;

//     // Initial update
//     updateArrows();

//     // Update on map events
//     map.on("move", updateArrows);
//     map.on("zoom", updateArrows);
//     map.on("resize", updateArrows);

//     return () => {
//       map.off("move", updateArrows);
//       map.off("zoom", updateArrows);
//       map.off("resize", updateArrows);
//     };
//   }, [map, updateArrows]);

//   const handleArrowHover = (
//     arrow: (typeof arrows)[0],
//     event: React.MouseEvent
//   ) => {
//     const rect = containerRef.current?.getBoundingClientRect();
//     if (!rect) return;

//     setTooltip({
//       x: event.clientX - rect.left,
//       y: event.clientY - rect.top,
//       relationship: arrow.relationship,
//       visible: true,
//     });
//   };

//   const handleArrowLeave = () => {
//     setTooltip(null);
//   };

//   // Color based on match type
//   const getArrowColor = (matchType: string): string => {
//     if (matchType === "source") return "#8cff00ff";
//     if (matchType === "src_owner") return "#f3ec21ff";
//     if (matchType === "address_1") return "#ff6a00ff";
//     if (matchType === "geocoded_address") return "#ce1140ff";
//     if (matchType === "people_owner") return "#E91E63";
//     if (matchType.startsWith("by_business")) return "#00d491ff";
//     return "#607D8B";
//   };

//   // Use portal to render into the injected container
//   if (!svgContainerRef.current) return null;

//   return ReactDOM.createPortal(
//     <>
//       <svg
//         className="relationship-arrows-svg"
//         style={{
//           position: "absolute",
//           top: 0,
//           left: 0,
//           width: "100%",
//           height: "100%",
//           pointerEvents: "none",
//           overflow: "visible",
//           background: "transparent",
//           backgroundColor: "transparent",
//         }}
//       >
//         <defs>
//           {/* Arrow markers for each color */}
//           {[
//             "#4CAF50",
//             "#2196F3",
//             "#FF9800",
//             "#9C27B0",
//             "#E91E63",
//             "#00BCD4",
//             "#607D8B",
//           ].map((color) => (
//             <marker
//               key={color}
//               id={`arrowhead-${color.replace("#", "")}`}
//               markerWidth="10"
//               markerHeight="7"
//               refX="9"
//               refY="3.5"
//               orient="auto"
//               markerUnits="strokeWidth"
//             >
//               <polygon points="0 0, 10 3.5, 0 7" fill={color} />
//             </marker>
//           ))}
//         </defs>

//         {arrows.map((arrow, idx) => {
//           const color = getArrowColor(arrow.relationship.matchType);
//           return (
//             <g key={idx}>
//               {/* Main arrow path */}
//               <path
//                 d={arrow.path}
//                 fill="none"
//                 stroke={color}
//                 strokeWidth="5"
//                 strokeOpacity="0.7"
//                 markerEnd={`url(#arrowhead-${color.replace("#", "")})`}
//                 style={{ cursor: "pointer", pointerEvents: "stroke" }}
//                 onMouseEnter={(e) => handleArrowHover(arrow, e)}
//                 onMouseLeave={handleArrowLeave}
//               />
//             </g>
//           );
//         })}
//       </svg>

//       {/* Tooltip */}
//       {tooltip && tooltip.visible && (
//         <div
//           className="arrow-tooltip"
//           style={{
//             position: "absolute",
//             left: Math.min(tooltip.x + 15, dimensions.width - 340),
//             top: Math.max(tooltip.y - 10, 10),
//             zIndex: 1000,
//             background: "rgba(20, 20, 30, 0.95)",
//             color: "white",
//             padding: "12px 16px",
//             borderRadius: "8px",
//             maxWidth: "320px",
//             boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
//             fontSize: "13px",
//             lineHeight: "1.5",
//             pointerEvents: "none",
//             backdropFilter: "blur(8px)",
//             border: `2px solid ${getArrowColor(
//               tooltip.relationship.matchType
//             )}`,
//           }}
//         >
//           <div
//             style={{
//               fontWeight: "bold",
//               marginBottom: "8px",
//               color: getArrowColor(tooltip.relationship.matchType),
//               fontSize: "14px",
//               textTransform: "uppercase",
//               letterSpacing: "0.5px",
//             }}
//           >
//             {formatMatchType(tooltip.relationship.matchType)}
//           </div>
//           <div style={{ whiteSpace: "pre-wrap", opacity: 0.95 }}>
//             {generateExplanation(tooltip.relationship)}
//           </div>
//         </div>
//       )}
//     </>,
//     svgContainerRef.current
//   );
// }

// // ============================================
// // AGGREGATION FUNCTIONS
// // ============================================

interface AggregatedPropertiesResult {
  highConfidence: GroupedLocation[];
  lowConfidenceBusinessOnly: GroupedLocation[];
}

function aggregateRelatedProperties(
  related: RelatedProperty[],
  related_by_cluster?: Record<string, RelatedPropertyByCluster[]>,
): AggregatedPropertiesResult {
  // First pass: collect all property IDs that appear in non-by_business::xxxx::by_address contexts
  const nonBusinessOnlyPropertyIds = new Set<string>();

  // Check related properties for non-business-only types
  related.forEach((prop) => {
    if (!isByBusinessByAddressType(prop.type)) {
      nonBusinessOnlyPropertyIds.add(prop.match);
    }
  });

  // Check related_by_cluster properties for non-business-only types
  if (related_by_cluster) {
    Object.values(related_by_cluster).forEach((properties) => {
      properties.forEach((prop) => {
        if (!isByBusinessByAddressType(prop.type)) {
          nonBusinessOnlyPropertyIds.add(prop.match);
        }
      });
    });
  }

  const highConfidenceLocationMap = new Map<string, GroupedLocation>();
  const lowConfidenceLocationMap = new Map<string, GroupedLocation>();
  const seenHighConfidencePropertyIds = new Set<string>();
  const seenLowConfidencePropertyIds = new Set<string>();

  // Helper to consolidate addresses for a location
  const consolidateLocationAddresses = (location: GroupedLocation) => {
    const validAddresses = location.addresses.filter(
      (a) => a.address_full && a.address_full.trim().length > 0,
    );

    if (validAddresses.length <= 1) {
      location.addresses = validAddresses;
      location.unitCount = validAddresses.length;
      return;
    }

    // Normalize and parse addresses
    const parsed = validAddresses.map((a) => {
      const normalized = a.address_full
        .toUpperCase()
        .trim()
        .replace(/\s+/g, " ");
      const unitMatch = normalized.match(
        /^(.+?)\s*(UNIT|APT|#|STE|SUITE)\s*(.+)$/i,
      );
      return {
        original: a,
        normalized,
        base: unitMatch ? unitMatch[1].trim() : normalized,
        unit: unitMatch ? unitMatch[3].trim() : null,
        hasUnit: !!unitMatch,
      };
    });

    // Group by base address
    const baseGroups = new Map<string, typeof parsed>();
    parsed.forEach((p) => {
      if (!baseGroups.has(p.base)) {
        baseGroups.set(p.base, []);
      }
      baseGroups.get(p.base)!.push(p);
    });

    const consolidatedAddresses: PropertyAddress[] = [];
    let totalUnitCount = 0;

    baseGroups.forEach((group, base) => {
      const baseEntry = group.find((p) => !p.hasUnit);

      if (baseEntry) {
        consolidatedAddresses.push(baseEntry.original);
        const unitsInGroup = group.filter((p) => p.hasUnit);
        totalUnitCount += Math.max(1, unitsInGroup.length);
      } else {
        consolidatedAddresses.push(group[0].original);
        totalUnitCount += group.length;
      }
    });

    location.addresses = consolidatedAddresses;
    location.unitCount = totalUnitCount;
  };

  // Helper to process a property into the appropriate map
  const processProperty = (
    prop: RelatedProperty | RelatedPropertyByCluster,
    clusterId?: string,
  ) => {
    if (!prop.addresses) return;

    const isBusinessOnlyType = isByBusinessByAddressType(prop.type);

    // If this property ONLY appears in by_business::xxxx::by_address context
    // (i.e., it's not in the nonBusinessOnlyPropertyIds set), it goes to low confidence
    const isLowConfidenceBusinessOnly =
      isBusinessOnlyType && !nonBusinessOnlyPropertyIds.has(prop.match);

    if (isLowConfidenceBusinessOnly) {
      // Process as low confidence
      if (seenLowConfidencePropertyIds.has(prop.match)) return;
      seenLowConfidencePropertyIds.add(prop.match);

      const key = `${prop.location.latitude},${prop.location.longitude}`;

      if (!lowConfidenceLocationMap.has(key)) {
        lowConfidenceLocationMap.set(key, {
          propertyId: prop.match,
          location: prop.location as PropertyLocation,
          addresses: [...prop.addresses] as PropertyAddress[],
          cluster_id: clusterId,
          type: prop.type,
          confidence: 0.5,
          isBusinessOnlyMatch: true,
        });
      } else {
        const existing = lowConfidenceLocationMap.get(key)!;
        const existingAddrs = new Set(
          existing.addresses.map((a) => a.address_full?.toUpperCase().trim()),
        );
        prop.addresses.forEach((addr) => {
          if (!existingAddrs.has(addr.address_full?.toUpperCase().trim())) {
            existing.addresses.push(addr as PropertyAddress);
          }
        });
        if (clusterId && !existing.cluster_id) {
          existing.cluster_id = clusterId;
        }
      }
    } else {
      // Process as high confidence (skip if it's a business-only type but property exists elsewhere)
      if (isBusinessOnlyType) return; // Skip - this property is already counted in high confidence from another source

      if (seenHighConfidencePropertyIds.has(prop.match)) return;
      seenHighConfidencePropertyIds.add(prop.match);

      const key = `${prop.location.latitude},${prop.location.longitude}`;

      if (!highConfidenceLocationMap.has(key)) {
        highConfidenceLocationMap.set(key, {
          propertyId: prop.match,
          location: prop.location as PropertyLocation,
          addresses: [...prop.addresses] as PropertyAddress[],
          cluster_id: clusterId,
          type: prop.type,
          confidence: prop.confidence,
          isBusinessOnlyMatch: false,
        });
      } else {
        const existing = highConfidenceLocationMap.get(key)!;
        const existingAddrs = new Set(
          existing.addresses.map((a) => a.address_full?.toUpperCase().trim()),
        );
        prop.addresses.forEach((addr) => {
          if (!existingAddrs.has(addr.address_full?.toUpperCase().trim())) {
            existing.addresses.push(addr as PropertyAddress);
          }
        });
        if (clusterId && !existing.cluster_id) {
          existing.cluster_id = clusterId;
        }
      }
    }
  };

  // Process related properties first
  related.forEach((prop) => processProperty(prop));

  // Process cluster properties
  if (related_by_cluster) {
    Object.entries(related_by_cluster).forEach(([clusterId, properties]) => {
      properties.forEach((prop) => processProperty(prop, clusterId));
    });
  }

  // Consolidate addresses for both maps
  const highConfidenceResult = Array.from(highConfidenceLocationMap.values());
  const lowConfidenceResult = Array.from(lowConfidenceLocationMap.values());

  highConfidenceResult.forEach(consolidateLocationAddresses);
  lowConfidenceResult.forEach(consolidateLocationAddresses);

  return {
    highConfidence: highConfidenceResult,
    lowConfidenceBusinessOnly: lowConfidenceResult,
  };
}

function getOwnerNames(apiResponse: NewAPIResponse): string[] {
  // Check for business chain first
  if (
    apiResponse.result.business_cluster_chain &&
    apiResponse.result.top_level.length > 0
  ) {
    const topLevel = apiResponse.result.top_level[0];
    if (topLevel.members && topLevel.members.length > 0) {
      return topLevel.members.map(
        (m) =>
          `${m.first_name}${m.middle_name ? " " + m.middle_name : ""} ${
            m.last_name
          }${m.title ? " " + m.title : ""}`,
      );
    }
    return [topLevel.business_name];
  }

  // Fall back to people_owners
  if (apiResponse.result.details.people_owners) {
    return apiResponse.result.details.people_owners.map((po) =>
      po.first_name
        ? `${po.first_name}${po.middle_name ? " " + po.middle_name : ""} ${
            po.last_name
          }${po.title ? " " + po.title : ""}`
        : (po.name as string),
    );
  }

  // Fall back to src_owners
  return (
    apiResponse.result.details.property_data.flatMap((pd) => pd.src_owner) || []
  );
}

// // ============================================
// // BUILD RELATIONSHIP CHAIN FROM API RESPONSE
// // ============================================

// function buildRelationshipChain(
//   apiResponse: NewAPIResponse,
//   sourceLocation: PropertyLocation
// ): PropertyRelationship[] {
//   const relationships: PropertyRelationship[] = [];
//   const relatedProperties = apiResponse.result.related;

//   // Find the source property
//   const sourceProperty = relatedProperties.find((p) => p.type === "source");
//   if (!sourceProperty) return relationships;

//   // Build a map of property_id to property data
//   const propertyMap = new Map<string, RelatedProperty>();
//   relatedProperties.forEach((prop) => {
//     propertyMap.set(prop.match, prop);
//   });

//   // Track which properties we've connected
//   const connectedProperties = new Set<string>();
//   connectedProperties.add(sourceProperty.match);

//   // For each non-source property, create a relationship
//   // We'll connect them based on their match_type and match_key
//   relatedProperties.forEach((prop) => {
//     if (prop.type === "source") return;
//     if (!prop.location?.latitude || !prop.location?.longitude) return;

//     // Find the best "from" property - look for one that shares the same match_key
//     let fromProperty: RelatedProperty | null = null;
//     let fromLocation = sourceLocation;

//     // First, check if we can find a property that this one was matched from
//     // based on the match_key
//     if (prop.match_key) {
//       // Look for another property with the same match_key that's already connected
//       for (const connectedId of Array.from(connectedProperties)) {
//         const connected = propertyMap.get(connectedId);
//         if (connected && connected.match !== prop.match) {
//           // Check if they share a connection
//           if (
//             connected.match_key === prop.match_key ||
//             connected.type === "source" ||
//             (connected.match_to && connected.match_to === prop.match_key)
//           ) {
//             fromProperty = connected;
//             fromLocation = connected.location;
//             break;
//           }
//         }
//       }
//     }

//     // If no specific from property found, connect to source
//     if (!fromProperty) {
//       fromProperty = sourceProperty;
//       fromLocation = sourceProperty.location;
//     }

//     relationships.push({
//       fromPropertyId: fromProperty.match,
//       toPropertyId: prop.match,
//       fromLocation: fromLocation,
//       toLocation: prop.location,
//       matchType: prop.type,
//       matchKey: prop.match_key || "",
//       matchTo: prop.match_to as string,
//       confidence: prop.confidence,
//     });

//     connectedProperties.add(prop.match);
//   });

//   // Also add relationships from cluster properties
//   if (apiResponse.result.related_by_cluster) {
//     Object.entries(apiResponse.result.related_by_cluster).forEach(
//       ([clusterId, properties]) => {
//         properties.forEach((prop) => {
//           if (!prop.location?.latitude || !prop.location?.longitude) return;
//           if (connectedProperties.has(prop.match)) return; // Already connected

//           // For cluster properties, try to connect to a property already in the cluster
//           let fromLocation = sourceLocation;

//           relationships.push({
//             fromPropertyId: sourceProperty.match,
//             toPropertyId: prop.match,
//             fromLocation: fromLocation,
//             toLocation: prop.location,
//             matchType: prop.type,
//             matchKey: clusterId,
//             confidence: prop.confidence,
//           });

//           connectedProperties.add(prop.match);
//         });
//       }
//     );
//   }

//   return relationships;
// }

// ============================================
// MAIN RESULT COMPONENT
// ============================================

function Result({
  apiResponse,
  closeResult,
}: {
  apiResponse: NewAPIResponse;
  closeResult: () => void;
}) {
  console.log(apiResponse);

  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const survey = useRef(null);
  const rightRef = useRef(null);

  const mainProperty = apiResponse.result.details.property_data[0];

  const property = apiResponse.result.related.find(
    (prop) => prop.type === "source",
  );
  const propertyLocation = property?.location!;
  const propertyAddress = property?.addresses[0].address_full;

  let possibleOwners = apiResponse.result.src_owners.concat(
    apiResponse.result.people_owners || [],
  );

  const chains = {} as BusClusterChain;
  for (const clusterId in apiResponse.result.business_cluster_chain) {
    chains[clusterId] = [
      ...apiResponse.result.business_cluster_chain[clusterId],
    ].reverse();
  }

  const [lng] = useState(propertyLocation.longitude);
  const [lat] = useState(propertyLocation.latitude);
  const [zoom] = useState(12);

  const aggregatedProperties = aggregateRelatedProperties(
    apiResponse.result.related,
    apiResponse.result.related_by_cluster,
  );

  const [locations] = useState(aggregatedProperties.highConfidence);
  const [lowConfidenceLocations] = useState(
    aggregatedProperties.lowConfidenceBusinessOnly,
  );

  const [selectedLocation, setSelectedLocation] = useState(
    null as GroupedLocation | null,
  );
  const [owners] = useState(getOwnerNames(apiResponse));
  const [scrolled, setScrolled]: [boolean, any] = useState(false);
  const [atBottom, setAtBottom]: [boolean, any] = useState(true);
  const [showSurvey, setShowSurvey]: [boolean, any] = useState(true);
  const [showJoin, setShowJoin] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showVacancy, setShowVacancy] = useState(false);
  const [showReportScreen, setShowReportScreen] = useState(false);
  const [hideReportButton, setHideReportButton] = useState(false);
  const [showPMSuggestion, setShowPMSuggestion] = useState(false);
  const [extraPadding, setExtraPadding] = useState(true);
  const [markersDisplayed, setMarkersDisplayed] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [showArrows, setShowArrows] = useState(false);
  const thanksRef = useRef<HTMLDivElement>(null);
  const [possibleOwnerStrings] = useState(
    possibleOwners.filter((p) => {
      for (const o of owners) {
        if (o) {
          let split = o.split(" ");
          if (split.every((segment) => p.includes(segment))) {
            return false;
          }
        }
      }
      return true;
    }),
  );
  const [businessChains] = useState(chains);
  const [showVacancies, setShowVacancies] = useState(false);
  const currentProperty = apiResponse.result.related.find(
    (r) => r.type === "source",
  );
  const currentAddress = currentProperty?.addresses[0]?.address_full
    .split("APT")[0]
    .trim()
    .split("APARTMENT")[0]
    .trim()
    .split("UNIT")[0]
    .trim()
    .split("STE")[0]
    .trim()
    .split("#")[0]
    .trim();
  // Dedup related properties by property_id
  const seenIds = new Set<string>();
  const dedupedRelated = apiResponse.result.related.filter((r) => {
    if (seenIds.has(r.match)) return false;
    seenIds.add(r.match);
    return true;
  });
  const [showingOwnerAddress, toggleOwnerAddress] = useState(true);
  const [showLowConfidenceProperties, setShowLowConfidenceProperties] =
    useState(false);

  const [landlordSuggestions, setLandlordSuggestions] = useState(
    apiResponse.suggestions?.filter((s: any) => s.type === "landlord"),
  );
  const [pmSuggestions, setPMSuggestions] = useState(
    apiResponse.suggestions?.filter((s: any) => s.type === "pm"),
  );

  // // Build relationship chain for arrows
  // const [relationships] = useState(() =>
  //   buildRelationshipChain(apiResponse, propertyLocation),
  // );

  const relatedPropertyIds = new Set(
    apiResponse.result.related.map((r) => r.match),
  );

  // Get cluster-only properties that are NOT by_address type
  const clusterOnlyHighConfidence: RelatedPropertyByCluster[] = [];
  if (apiResponse.result.related_by_cluster) {
    const seenClusterIds = new Set<string>();

    Object.values(apiResponse.result.related_by_cluster).forEach(
      (properties) => {
        properties.forEach((prop) => {
          // Not in related, not already seen, and NOT a by_address type
          if (
            !relatedPropertyIds.has(prop.match) &&
            !seenClusterIds.has(prop.match) &&
            !isByBusinessByAddressType(prop.type)
          ) {
            seenClusterIds.add(prop.match);
            clusterOnlyHighConfidence.push(prop);
          }
        });
      },
    );
  }
  // Only count high confidence properties for totals
  const totalMarketValue =
    // From related (excluding by_address-only properties)
    dedupedRelated
      .filter(
        (rel) =>
          !isByBusinessByAddressType(rel.type) ||
          apiResponse.result.related.some(
            (r) => r.match === rel.match && !isByBusinessByAddressType(r.type),
          ),
      )
      .reduce((sum, rel) => sum + (rel.market_value || 0), 0) +
    // From cluster-only (by_member and by_business types)
    clusterOnlyHighConfidence.reduce(
      (sum, prop) => sum + (prop.market_value || 0),
      0,
    );

  // Units = sum of unitCount (which includes collapsed units) - HIGH CONFIDENCE ONLY
  const totalUnits = locations.reduce(
    (sum, loc) => sum + (loc.unitCount || loc.addresses.length || 1),
    0,
  );

  const totalAddresses = locations.reduce(
    (sum, loc) => sum + loc.addresses.length,
    0,
  );

  // Low confidence totals (for display purposes)
  const lowConfidenceTotalUnits = lowConfidenceLocations.reduce(
    (sum, loc) => sum + (loc.unitCount || loc.addresses.length || 1),
    0,
  );

  const lowConfidenceTotalAddresses = lowConfidenceLocations.reduce(
    (sum, loc) => sum + loc.addresses.length,
    0,
  );

  const evictionRange =
    apiResponse.result.evictions?.length &&
    getEvictionRange(
      apiResponse.result.evictions,
      apiResponse.result.evictions_by_cluster,
    );
  useEffect(() => {
    if (!showSurvey && thanksRef.current) {
      thanksRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [showSurvey]);

  useEffect(() => {
    if (map.current) return;

    const mapOptions: any = {
      container: mapContainer.current,
      style: config.MAPBOX_STYLE,
      zoom: zoom,
      center: [lng, lat],
      maxZoom: 16,
    };

    map.current = new mapboxgl.Map(mapOptions);

    // Add main property marker
    const mainMarker = document.createElement("div");
    mainMarker.className = "main-marker";
    new mapboxgl.Marker(mainMarker).setLngLat([lng, lat]).addTo(map.current);

    // Add related property markers
    showAllLocations();

    // // Set map ready after load
    // map.current.on("load", () => {
    //   setMapReady(true);
    // });

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      setAtBottom(entry.isIntersecting);
    });

    if (survey && survey.current) {
      observer.observe(survey.current!);
    }
  });

  function showAllLocations() {
    if (markersDisplayed) return;

    const coords: Array<[number, number]> = [[lng, lat]];

    apiResponse.result.property_owner_address
      ?.filter((addr) => addr.latitude)
      .forEach((addr) => {
        console.log(addr);
        const el = document.createElement("div");
        el.className = "owner-home-marker";
        const latlng: [number, number] = [addr.longitude, addr.latitude];
        new mapboxgl.Marker(el).setLngLat(latlng).addTo(map.current);
        if (config.STATE_NAMES.includes(addr.state.toUpperCase())) {
          coords.push(latlng);
        }
      });

    // Add HIGH CONFIDENCE markers
    locations
      .filter((loc) => loc.addresses)
      .filter(
        (loc) =>
          loc.addresses.length > 0 &&
          loc.addresses[0].address_full?.trim() &&
          loc.location.latitude,
      )
      .forEach((loc) => {
        const latlng: [number, number] = [
          loc.location.longitude,
          loc.location.latitude,
        ];

        if (
          latlng[0] === lng &&
          latlng[1] === lat &&
          loc.addresses.length < 2
        ) {
          return;
        }

        const el = document.createElement("div");

        if (loc.addresses.length > 1) {
          el.innerHTML = `<span class="dot">${loc.addresses.length}</span>`;
        }

        // Set marker class based on cluster
        if (loc.confidence < 0.75) {
          el.className = "marker question-marker";
        } else if (loc.cluster_id) {
          el.setAttribute("data-cluster", loc.cluster_id);
          const businessIds = [];
          for (const chains of Object.values(businessChains)) {
            for (const chain of chains) {
              businessIds.push(chain.info.reg_num);
            }
          }
          const businessId = loc.type.match(
            /^by_business::([A-Z|0-9]+)::by_address$/,
          )?.[1];
          if (businessId && !businessIds.includes(businessId)) {
            el.className = "marker";
          } else {
            el.className = "marker cluster-marker";
          }
        } else {
          el.className = "marker";
        }

        el.onclick = () => {
          setSelectedLocation(loc);
        };

        new mapboxgl.Marker(el).setLngLat(latlng).addTo(map.current);
        coords.push(latlng);
      });

    // Add LOW CONFIDENCE markers (different style)
    lowConfidenceLocations
      .filter((loc) => loc.addresses)
      .filter(
        (loc) =>
          loc.addresses.length > 0 &&
          loc.addresses[0].address_full?.trim() &&
          loc.location.latitude,
      )
      .forEach((loc) => {
        const latlng: [number, number] = [
          loc.location.longitude,
          loc.location.latitude,
        ];

        if (
          latlng[0] === lng &&
          latlng[1] === lat &&
          loc.addresses.length < 2
        ) {
          return;
        }

        const el = document.createElement("div");

        if (loc.addresses.length > 1) {
          el.innerHTML = `<span class="dot">${loc.addresses.length}</span>`;
        }

        // Always use question-marker style for low confidence
        el.className = "marker question-marker low-confidence-marker";

        el.onclick = () => {
          setSelectedLocation(loc);
        };

        new mapboxgl.Marker(el).setLngLat(latlng).addTo(map.current);
        // Don't add to coords for bounds fitting - these are low confidence
      });

    if (coords.length > 1) {
      const bounds = new mapboxgl.LngLatBounds();
      coords.forEach((coord) => bounds.extend(coord));
      (map.current as any).fitBounds(bounds, { padding: 60 });
    }

    setMarkersDisplayed(true);
  }

  function handleScroll(event: React.UIEvent<HTMLDivElement>) {
    const target = event.target as any;
    setScrolled(target.scrollTop > 20);
  }

  function scrollToReviews() {
    setScrolled(true);
    setExtraPadding(false);
    setTimeout(() => setExtraPadding(true), 2000);

    if (survey && survey.current) {
      (survey.current as any).scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
  }

  function hideSurvey(showJoin: boolean) {
    setShowJoin(showJoin);
    setShowSurvey(false);
  }

  function hideAllMarkerMenus() {
    setSelectedLocation(null);
  }

  const resultRef = useRef(null);

  return (
    <div className="result" ref={resultRef}>
      <Disclaimer callback={null} />
      {showDisclaimer && (
        <Disclaimer callback={(val) => setShowDisclaimer(val)} />
      )}
      {showReportScreen && (
        <ReportScreen
          property_id={mainProperty?.property_id}
          address={currentAddress}
          callback={(hide) => {
            setHideReportButton(hide);
            setShowReportScreen(false);
          }}
        />
      )}
      {showPMSuggestion && (
        <PMSuggestion
          property_id={mainProperty?.property_id}
          callback={() => {
            setShowPMSuggestion(false);
          }}
        />
      )}
      {showVacancy && (
        <VacancySuggestion
          property_id={mainProperty?.property_id}
          address={propertyAddress}
          callback={() => {
            setShowVacancy(false);
          }}
        />
      )}
      <div className="headerContainer small-header"></div>

      <div className="result-container">
        <div
          className="left"
          // style={{ position: "relative", overflow: "hidden" }}
        >
          {selectedLocation && (
            <div className="screen" onClick={() => hideAllMarkerMenus()}></div>
          )}
          {selectedLocation && (
            <>
              <div
                className="screen"
                onClick={() => hideAllMarkerMenus()}
              ></div>
              <div className="address-menu-container">
                <div
                  className="menu-container"
                  onClick={(ev) => ev.stopPropagation()}
                >
                  <div className="title">
                    Properties at this Location
                    {selectedLocation.isBusinessOnlyMatch && (
                      <span className="low-confidence-badge">
                        {" "}
                        (Low Confidence)
                      </span>
                    )}
                  </div>
                  {selectedLocation.addresses.map((a) => (
                    <div className="menu-item" key={a.address_full}>
                      <div className="address">
                        <a
                          className="menu-link"
                          href={`/property/${encode(
                            selectedLocation.propertyId,
                          )}`}
                        >
                          {a.address_full}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Map wrapper with arrows overlay */}
          {/* <div
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              zIndex: 10000,
            }}
          > */}
          <div ref={mapContainer} className="map-container"></div>

          {/* Relationship Arrows Overlay - inside map wrapper to clip properly */}
          {/* {mapReady && showArrows && map.current && (
              <RelationshipArrows
                relationships={relationships}
                map={map.current}
                containerRef={mapContainer}
              />
            )} */}

          {/* Arrow Toggle Button */}
          {/* <button
              className="arrow-toggle-btn"
              onClick={() => setShowArrows(!showArrows)}
              style={{
                position: "absolute",
                bottom: "20px",
                left: "20px",
                zIndex: 20000,
                padding: "8px 16px",
                background: showArrows
                  ? "rgba(187, 243, 33, 0.9)"
                  : "rgba(100, 100, 100, 0.8)",
                color: showArrows ? "black" : "white",
                border: "none",
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
                boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s ease",
                pointerEvents: "auto",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
              {showArrows ? "Hide Connections" : "Show Connections"}
            </button> */}
          {/* </div> */}
        </div>

        <div
          className={scrolled ? "right right-up" : "right right-down"}
          onScroll={handleScroll}
          ref={rightRef}
        >
          <div className="top-buttons">
            <button onClick={() => setShowDisclaimer(true)}>
              View Disclaimer
            </button>
          </div>

          <div className="data-container">
            <h3 className="address-title-container">
              {mainProperty && (
                <div className="address-container">
                  <div className="address-title">{currentAddress}</div>
                  <div className="address-title">
                    {currentProperty?.city}, {currentProperty?.state_abbrev}
                  </div>
                </div>
              )}
              <div className="main-marker-inline" />
            </h3>

            <div className="owner-container">
              <div className="owned_by">
                likely owned by
                {!hideReportButton && (
                  <div className="report-button">
                    <button onClick={() => setShowReportScreen(true)}>
                      <div></div>
                    </button>
                  </div>
                )}
              </div>
              {owners.map((o, i) => (
                <React.Fragment key={i}>
                  {i !== 0 && <div className="ampersand">&amp;</div>}
                  <div className="owner-name">{o}</div>
                </React.Fragment>
              ))}
              {apiResponse.result.business_cluster_chain &&
                Object.keys(apiResponse.result.business_cluster_chain).map(
                  (cluster_id, i) => (
                    <div className="via-wrapper" key={cluster_id}>
                      <div className="via-text">through</div>

                      <div>
                        {apiResponse.result.business_cluster_chain[
                          cluster_id
                        ].map((business, i, arr) => (
                          <div key={i}>
                            <div
                              className={
                                "via" +
                                (i !== arr.length - 1 ? " arrow-down" : "")
                              }
                            >
                              <div>{business.info.business_name}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ),
                )}
              {landlordSuggestions.length > 0 && (
                <SuggestionBox
                  initalSuggestions={landlordSuggestions}
                  landlord={true}
                />
              )}
              {pmSuggestions.length > 0 && (
                <SuggestionBox initalSuggestions={pmSuggestions} />
              )}
              <div className="suggested-pms-container">
                <button onClick={() => setShowPMSuggestion(true)}>
                  suggest a property management company
                </button>
              </div>
            </div>

            {evictionRange != 0 && (
              <div className="evictions-wrapper">
                <div className="evictions">{evictionRange}</div>
              </div>
            )}

            <div className="properties">
              {/* HIGH CONFIDENCE PROPERTIES */}
              {locations && locations.length > 0 && (
                <div className="locations-container">
                  <div className="market-value-str">this owner owns</div>
                  <div className="counts-container">
                    <div className="counts-element">
                      <span className="count-number">{totalUnits}</span>
                      <br />
                      units
                    </div>
                    <div className="counts-element">
                      <span className="count-number">{totalAddresses}</span>
                      <br />
                      {totalAddresses === 1 ? "address" : "addresses"}
                    </div>
                    {locations.length > 1 && (
                      <div className="counts-element">
                        <span className="count-number">{locations.length}</span>
                        <br />
                        locations
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* LOW CONFIDENCE PROPERTIES - Separate section */}
              {/* {lowConfidenceLocations && lowConfidenceLocations.length > 0 && (
                <div className="locations-container low-confidence-section">
                  <div className="market-value-str">
                    <span className="low-confidence-label">
                      possibly related properties (low confidence)
                    </span>
                    <button
                      className="toggle-low-confidence"
                      onClick={() =>
                        setShowLowConfidenceProperties(
                          !showLowConfidenceProperties
                        )
                      }
                    >
                      {showLowConfidenceProperties ? "hide" : "show"}
                    </button>
                  </div>
                  {showLowConfidenceProperties && (
                    <>
                      <div className="counts-container low-confidence-counts">
                        <div className="counts-element">
                          <span className="count-number">
                            {lowConfidenceTotalUnits}
                          </span>
                          <br />
                          units
                        </div>
                        <div className="counts-element">
                          <span className="count-number">
                            {lowConfidenceTotalAddresses}
                          </span>
                          <br />
                          {lowConfidenceTotalAddresses === 1
                            ? "address"
                            : "addresses"}
                        </div>
                        {lowConfidenceLocations.length > 1 && (
                          <div className="counts-element">
                            <span className="count-number">
                              {lowConfidenceLocations.length}
                            </span>
                            <br />
                            locations
                          </div>
                        )}
                      </div>
                      <div className="low-confidence-note">
                        These properties are linked only through business
                        address matches and may not be owned by the same entity.
                      </div>
                    </>
                  )}
                </div>
              )} */}

              {totalMarketValue > 0 && (
                <>
                  <div className="market-value-str">
                    total market-value of properties:
                  </div>
                  <div className="market-value">
                    {convertToUSD(totalMarketValue)}
                  </div>
                </>
              )}

              <div className="owner-address-wrapper">
                {showingOwnerAddress && (
                  <div className="marker-inline-wrapper">
                    <br />
                    <div className="market-value-str">owner's address:</div>
                    <div className="owner-address">
                      {apiResponse.result.property_owner_address?.map(
                        (a, idx) => (
                          <div className="owner-address-line" key={idx}>
                            <div className="owner-marker-inline" />
                            <span className="owner-address">
                              {a.address_1}
                              {a.address_2 ? +" " + a.address_2 : ""}
                              <br />
                              {a.city} {a.state} {a.zip}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Related Names Section */}
            {possibleOwnerStrings &&
              Object.keys(possibleOwnerStrings).length > 0 && (
                <div className="data-box">
                  <div className="title">Related Owners</div>
                  <div className="box">
                    {possibleOwnerStrings.map((name, idx) => (
                      <li key={`${idx}`} className="related-name-item">
                        {name}
                      </li>
                    ))}
                  </div>
                </div>
              )}

            {/* Related Businesses Section */}
            {/* {businessChains && Object.keys(businessChains).length > 0 && (
              <div className="data-box">
                <div className="title">Related Businesses</div>
                <div className="box">
                  {Object.entries(businessChains).map(
                    ([clusterId, chain]: [any, any]) =>
                      chain.map((business: any, idx: number) => (
                        <div
                          key={`${clusterId}-${idx}`}
                          className="related-business-item"
                        >
                          <div className="business-name">
                            {business.info.business_name}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            )} */}
            <div className="button-wrapper">
              <button
                className="report-vacancy"
                onClick={() => setShowVacancy(true)}
              >
                <Twemoji options={{ className: "small-emoji" }}>🚪</Twemoji>
                Report a Vacant Unit
              </button>
            </div>

            {/* <Modal
              button={
                <button className="report-vacancy">Report a Vacant Unit</button>
              }
              onSubmit={() =>
                submitVacancy({
                  propertyId: mainProperty?.property_id,
                  address: vacantAddress,
                  unit: vacantUnit,
                  sourceSystem: config.CITY,
                })
              }
            >
              <h3>Would you like to report a vacant unit?</h3>
              <label>Address</label>
              <input type="text" disabled value={vacantAddress} />
              <label>Unit</label>
              <input
                type="text"
                value={vacantUnit!}
                onChange={(event) => setVacantUnit(event.target.value)}
              />
            </Modal> */}
            {apiResponse.vacancies && apiResponse.vacancies.length > 0 && (
              <div className="vacancies-wrapper">
                <button
                  className="vacancies-toggle"
                  onClick={() => setShowVacancies(!showVacancies)}
                >
                  <span className="vacancies-count">
                    {apiResponse.vacancies.length}
                  </span>{" "}
                  {apiResponse.vacancies.length === 1 ? "VACANCY" : "VACANCIES"}{" "}
                  REPORTED
                  <span className="toggle-icon">
                    {showVacancies ? "▲" : "▼"}
                  </span>
                </button>
                {showVacancies && (
                  <div className="vacancies-list">
                    {apiResponse.vacancies.map((vacancy, idx) => (
                      <div key={idx} className="vacancy-item">
                        <span className="vacancy-address">
                          {vacancy.address}
                        </span>
                        {vacancy.unit && (
                          <>
                            <br />
                            <span className="vacancy-unit">
                              {" "}
                              Unit {vacancy.unit}
                            </span>
                          </>
                        )}
                        <br />
                        <span className="reported-on">
                          Reported on{" "}
                          {new Date(vacancy.reported_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Business hierarchy section would go here */}
            {/* {businessChains && Object.keys(businessChains).length > 0 && (
              <div className="business-info">
                <div className="business-data">
                  <div className="related-businesses-title">
                    Business Structure
                  </div>
                </div>
              </div>
            )} */}
            {/* Reviews section */}
            <div className="ratings-wrapper">
              <Reviews
                property_reviews={apiResponse.reviews}
                other_reviews={apiResponse.relatedReviews}
                onOpen={null}
                scrollToReviews={scrollToReviews}
              />
            </div>

            <div ref={survey} className="survey-container">
              {showSurvey ? (
                <Survey
                  landlordList={owners.map((name) => ({
                    name,
                    origin: "business_names",
                  }))}
                  hideSurvey={hideSurvey}
                  address={
                    apiResponse.result.related[0]?.addresses?.[0]
                      ?.address_full || ""
                  }
                  propertyId={mainProperty?.property_id}
                />
              ) : (
                <div className="thanks" ref={thanksRef}>
                  <p>Thanks for your feedback!</p>
                  {config.CITY === "PDX" && (
                    <div className="action-items">
                      <p>
                        click here if you want to learn how to organize a tenant
                        union!
                      </p>
                      <button
                        onClick={() => {
                          window.location.href =
                            "https://ranpdx.org/org-my-building";
                        }}
                        className="action green"
                      >
                        Organizing a Tenant Union
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            {config.CITY === "PDX" && (
              <div className="join-wrapper">
                {!showSurvey && showJoin && (
                  <Join
                    text="Signup to get connected with neighbors who also review this landlord/property and join Renters Action Network"
                    property_id={mainProperty?.property_id}
                  />
                )}
              </div>
            )}

            <div className="buffer"></div>
          </div>
        </div>
      </div>

      {showSurvey && (
        <div className="jump-to-reviews-wrapper">
          <button
            className={
              "jump-to-reviews " + (atBottom ? "hide-jump" : "show-jump")
            }
            onClick={() => scrollToReviews()}
          >
            leave a review
          </button>
        </div>
      )}
    </div>
  );
}

export default Result;
